import type { Application } from '@hotwired/stimulus';
import { Controller } from '@hotwired/stimulus';

export class MyContentItemGroupsController extends Controller<HTMLElement> {
    declare readonly overlayContainerTarget: HTMLElement;
    declare readonly overlayToggleTarget: HTMLElement;
    declare readonly dropdownContainerTarget: HTMLElement;
    declare readonly dropdownToggleTarget: HTMLElement;
    declare readonly gradeInputTargets: HTMLInputElement[];
    declare readonly additionalInputTargets: HTMLInputElement[];

    declare bodyOnOverlayClass: string;
    declare bodyOnOverlayClasses: string[];
    declare readonly hasBodyOnOverlayClass: boolean;

    static targets = [
        'overlayContainer',
        'overlayToggle',
        'dropdownContainer',
        'dropdownToggle',
        'gradeInput',
        'additionalInput',
    ];
    static classes = ['bodyOnOverlay'];

    static register(application: Application) {
        application.register('my-content-item-groups', this);
    }

    private viewportLargeMediaQueryList: MediaQueryList;

    connect() {
        this.viewportLargeMediaQueryList = matchMedia('screen and (min-width: 50em)');
        this.viewportLargeMediaQueryList.addEventListener('change', this.matchMediaChangeListener.bind(this));

        this.switchLargeLayout(this.viewportLargeMediaQueryList.matches);
    }

    disconnect(): void {
        this.viewportLargeMediaQueryList.removeEventListener('change', this.matchMediaChangeListener.bind(this));
    }

    /**
     * Enable dropdown and disable overlay on large screens.
     * Vice versa on non-large screens.
     * @param isLarge
     */
    switchLargeLayout(isLarge: boolean) {
        const dropdownExpanded = this.dropdownToggleTarget.getAttribute('aria-expanded') === 'true';

        if (isLarge) {
            this.dropdownContainerTarget.toggleAttribute('hidden', !dropdownExpanded);
            this.overlayContainerTarget.toggleAttribute('hidden', false);
        } else {
            this.dropdownContainerTarget.toggleAttribute('hidden', false);
            this.overlayContainerTarget.toggleAttribute('hidden', !dropdownExpanded);
        }
    }

    toggleOverlay(_event: MouseEvent, force: boolean) {
        const showOverlay = force ?? this.overlayToggleTarget.getAttribute('aria-expanded') !== 'true';

        this.overlayToggleTarget.setAttribute('aria-expanded', showOverlay ? 'true' : 'false');
        this.overlayContainerTarget.toggleAttribute('hidden', !showOverlay);

        document.body.classList.toggle(this.bodyOnOverlayClass, showOverlay);
    }

    toggleDropdown(_event: MouseEvent, force: boolean) {
        const showDropdown = force ?? this.dropdownToggleTarget.getAttribute('aria-expanded') !== 'true';

        this.dropdownContainerTarget.toggleAttribute('hidden', !showDropdown);
        this.dropdownToggleTarget.setAttribute('aria-expanded', showDropdown ? 'true' : 'false');
    }

    /**
     * Reset all checkboxes in the form.
     */
    reset() {
        [...this.gradeInputTargets, ...this.additionalInputTargets].forEach((input) => (input.checked = false));
    }

    /**
     * Check an element of the `additionalInputTargets` and submit the form afterwards.
     * Utilized on large screens to enable functionality on the additional filters without the filter submit button being visible/inside the dropdown.
     * @param event
     */
    checkAndSubmit(event: InputEvent & { params: { target: string } }) {
        const targetInput = this.additionalInputTargets.find((input) => input.name === event.params.target);

        targetInput.checked = (event.target as HTMLInputElement).checked;
        targetInput.form.submit();
    }

    private matchMediaChangeListener(event: MediaQueryListEvent) {
        this.switchLargeLayout(event.matches);
    }
}
